import { type UUID } from '~/types/common';

import { useQueryUser } from './useQueryUser';

/**
 * Resolves whether the current user is a synced (master-data) user.
 *
 * Trusts an explicit top-level `isSynced` boolean from `/user/data` (`GET
 * /user/data`) when present. Only when that flag is absent does it fall back
 * to `GET /user/{userId}`'s own `isSynced` field — this keeps older
 * `/user/data` responses (before the field existed) from leaving synced
 * users editable. Shared by `useAccountViewModel` and the legacy
 * `ProfileDetails` edit form so both resolve the same way.
 * @param userId - The current user's id, if known yet.
 * @param isSynced - The top-level `isSynced` flag from `/user/data`, if present.
 * @returns Whether the current user is a synced (master-data) user.
 */
export function useIsUserSynced(
  userId: UUID | undefined,
  isSynced: boolean | undefined,
): boolean {
  const { data: ownUserIsSynced } = useQueryUser(userId ?? '', {
    enabled: Boolean(userId) && isSynced === undefined,
    retry: false,
    select: (user) => Boolean(user.isSynced),
    throwOnError: false,
  });

  return isSynced ?? Boolean(ownUserIsSynced);
}
